import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';

const SAFE_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Serializes a query bag into a URL search string (no leading `?`), e.g. `{ a: '1', b: 2 }` →
 * `"a=1&b=2"`. Keys whose value is `null`/`undefined` are omitted; an empty or absent bag yields `""`.
 * A drop-in replacement for `new URLSearchParams(query).toString()` that also drops nullish values.
 */
export function buildSearchString(query?: Record<string, string | number | boolean | null | undefined>): string {
  if (query == null) {
    return '';
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value != null) {
      params.set(key, String(value));
    }
  }
  return params.toString();
}

/**
 * Returns true when `href` uses a safe protocol (http/https).
 * Rejects javascript:, data:, and other dangerous schemes.
 */
export function isSafeUrl(href: string): boolean {
  try {
    const url = new URL(href, window.location.origin);
    return SAFE_PROTOCOLS.has(url.protocol);
  } catch {
    return false;
  }
}

/**
 * Returns true when `href` is a fully-qualified http(s) URL.
 *
 * Unlike {@link isSafeUrl}, nothing is resolved against the current origin, so a
 * bare `"my-bucket"` is rejected rather than being read as a path. Use this when
 * validating a URL a user typed for a *server* to fetch — the back end parses it
 * with no base of its own.
 */
export function isAbsoluteHttpUrl(href: string): boolean {
  try {
    return SAFE_PROTOCOLS.has(new URL(href).protocol);
  } catch {
    return false;
  }
}

/**
 * The host of a fully-qualified http(s) URL, with its port, or null for
 * anything else: Ember's `httpUrlHost`.
 */
export function httpUrlHost(href: string | null | undefined): string | null {
  if (href == null || !isAbsoluteHttpUrl(href)) {
    return null;
  }
  return new URL(href).host;
}

/**
 * Checks whether a URL points to the current origin (in-platform link).
 * Returns the relative path if internal, or null if external.
 * Rejects dangerous protocols (javascript:, data:, etc.) even if they
 * somehow resolve to the same origin.
 */
export function isInternalLink(href: string): { relativePath: string } | null {
  try {
    const url = new URL(href, window.location.origin);
    if (!SAFE_PROTOCOLS.has(url.protocol)) return null;
    if (url.origin === window.location.origin) {
      return { relativePath: url.pathname + url.search + url.hash };
    }
  } catch {
    // Invalid URL
  }
  return null;
}

/**
 * Checks whether a same-origin URL belongs to the current workspace.
 * Returns false if the link targets a different workspace, which helps
 * avoid jarring in-page navigation to a different workspace context.
 * Returns true for non-workspace paths (e.g. /login, /select-workspace).
 */
export function isSameWorkspaceLink(href: string): boolean {
  const currentWorkspaceId = getWorkspaceIdFromUrl(window.location.pathname);
  const linkWorkspaceId = getWorkspaceIdFromUrl(href);

  // If either path doesn't contain a workspace ID, treat as same workspace
  if (currentWorkspaceId == null || linkWorkspaceId == null) return true;

  return currentWorkspaceId === linkWorkspaceId;
}
